import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import { Controller, useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { intlMessage, tagsPropTypes } from 'constants/propTypes';
import { bem } from 'helpers/class-name';
import { Card, CardTitle } from 'components/Card';
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'components/Dropdown2';
import Message from 'components/Message';
import Prompt from 'components/Prompt';
import SaveBar from 'components/SaveBar';
import { Title } from 'features/App';
import { ErrorMessage, Form, PhoneNumberInput, Switch, Tag, Tags } from 'components/HookForm';
import LoadingDots from 'components/LoadingDots';
import appMessages from 'features/App/App.messages';
import MarketingEmails from './MarketingEmails';
import messages, { notificationTypeMessages } from './Notifications.messages';
import { NOTIFICATION_TYPE, NOTIFICATION_TYPE_LIST } from './Notifications.constants';
import './Notifications.scss';

const FORM_NAME = 'NOTIFICATIONS_FORM';
const { block, element } = bem('notifications');

const PhoneNumberInputSection = ({ disabled, required }) => (
  <div className={element('phone-input-section')}>
    <Message as="label" className="text--light pb-s" message={messages.phoneNumberInput} htmlFor="userPhoneNumber" />
    <PhoneNumberInput
      name="userPhoneNumber"
      id="userPhoneNumber"
      className="mv-s"
      countries={['US']}
      addInternationalOption={false}
      countryCode="US"
      disabled={disabled}
      required={required}
    />
  </div>
);

PhoneNumberInputSection.propTypes = {
  disabled: PropTypes.bool,
  required: PropTypes.bool,
};

const NotificationGroup = ({
  notificationFieldStyle,
  notificationTreeName,
  notificationMessage,
  tree,
  type,
  showTitle,
  shouldShowPhoneInput,
  validate,
  disabled,
  required,
}) => {
  const { setValue, watch } = useFormContext();
  const allVenueIds = tree.map(({ id }) => id);
  const currentValues = watch(`receiveNotifications.${type}`);
  const isChecked = !!allVenueIds.every(venueId => currentValues.includes(venueId));

  const handleTreeAllSelected = useCallback(
    event => {
      return event.target.checked
        ? setValue(notificationTreeName, [...allVenueIds], { shouldDirty: true })
        : setValue(notificationTreeName, [], { shouldDirty: true });
    },
    [notificationTreeName, allVenueIds, setValue]
  );

  return (
    <>
      <Card className={notificationFieldStyle}>
        {showTitle && <Message as={CardTitle} message={notificationMessage.title} />}
        <Message as="p" className="text--light" message={notificationMessage.description} />
        <label className={block({ disabled })}>
          <input
            name="treeAllSelected"
            className={element('select-all', { disabled })}
            type="checkbox"
            checked={isChecked}
            onChange={handleTreeAllSelected}
            disabled={disabled}
          />
          <Message message={messages.selectAll} className={element('label', { disabled })} />
        </label>
        <Tags
          name={notificationTreeName}
          className={element('select-notifications')}
          validate={validate}
          tags={tree}
          onChange={(value, checked, activeTagIds = []) =>
            checked ? activeTagIds.concat(value) : activeTagIds.filter(venueId => venueId !== value)
          }>
          {({ id, name }) => <Tag name={id} value={id} label={name} disabled={disabled} />}
        </Tags>
        {shouldShowPhoneInput && <PhoneNumberInputSection disabled={disabled} required={required} />}
      </Card>
    </>
  );
};

NotificationGroup.propTypes = {
  notificationFieldStyle: PropTypes.string,
  notificationTreeName: PropTypes.string,
  notificationMessage: PropTypes.shape({
    title: intlMessage,
    description: intlMessage,
  }),
  tree: tagsPropTypes,
  type: PropTypes.oneOf(NOTIFICATION_TYPE_LIST),
  showTitle: PropTypes.bool,
  shouldShowPhoneInput: PropTypes.bool,
  validate: PropTypes.shape({
    message: intlMessage,
    value: PropTypes.func,
  }),
  disabled: PropTypes.bool,
  required: PropTypes.bool,
};

const SmsVenueDropdown = ({ tree, enabled }) => {
  const intl = useIntl();
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const name = `receiveNotifications.${NOTIFICATION_TYPE.CAN_RECEIVE_SMS}`;
  const allVenueIds = tree.map(({ id }) => id);
  const hasError = !!_get(errors, name, false);

  return (
    <div className="mv-s">
      <Controller
        control={control}
        name={name}
        rules={{ validate: value => !enabled || value.length > 0 || intl.formatMessage(messages.requiredTag) }}
        render={({ field: { value, onChange } }) => {
          const isAllVenues = allVenueIds.every(id => value.includes(id));
          const selectedVenue = isAllVenues ? undefined : tree.find(({ id }) => id === value[0]);

          let toggleContent;
          if (isAllVenues) {
            toggleContent = <Message message={messages.allVenues} />;
          } else if (selectedVenue) {
            toggleContent = <Message message={selectedVenue.name} />;
          } else {
            toggleContent = <Message className="text--light" message={messages.selectVenue} />;
          }

          return (
            <Dropdown disabled={!enabled}>
              <DropdownToggle hasError={hasError} disabled={!enabled}>
                {toggleContent}
              </DropdownToggle>
              <DropdownMenu>
                <DropdownItem isSelected={isAllVenues} onClick={() => onChange([...allVenueIds])}>
                  <Message message={messages.allVenues} />
                </DropdownItem>
                {tree.map(venue => (
                  <DropdownItem
                    key={venue.id}
                    isSelected={!isAllVenues && value[0] === venue.id}
                    onClick={() => onChange([venue.id])}>
                    <Message message={venue.name} />
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
          );
        }}
      />
      <ErrorMessage name={name} errors={errors} />
    </div>
  );
};

SmsVenueDropdown.propTypes = {
  tree: tagsPropTypes,
  enabled: PropTypes.bool,
};

const SmsSingleVenueSync = ({ venueId, enabled }) => {
  const { setValue } = useFormContext();
  const name = `receiveNotifications.${NOTIFICATION_TYPE.CAN_RECEIVE_SMS}`;
  const didMountRef = useRef(false);

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    setValue(name, enabled ? [venueId] : [], { shouldDirty: true });
  }, [enabled, venueId, name, setValue]);

  return null;
};

SmsSingleVenueSync.propTypes = {
  venueId: PropTypes.string,
  enabled: PropTypes.bool,
};

const Notifications = ({
  isLoading,
  notificationTypes,
  tagTrees,
  receiveNotifications,
  userSmsSubscriptionStatus,
  userPhoneNumber,
  handleSubmit,
}) => {
  return (
    <>
      {isLoading ? (
        <LoadingDots />
      ) : (
        <Form
          id={FORM_NAME}
          onSubmit={handleSubmit}
          isLoading={isLoading}
          defaultValues={{
            userSmsSubscriptionStatus,
            userPhoneNumber,
            receiveNotifications,
          }}>
          {({ watch, formState }) => {
            const { isDirty, isSubmitting } = formState;
            const isSmsEnabled = watch('userSmsSubscriptionStatus');

            return (
              <>
                <Prompt when={isDirty} />
                <Message as={Title} message={messages.title} />
                {notificationTypes.map(type => {
                  if (type === NOTIFICATION_TYPE.CAN_RECEIVE_SMS) {
                    return (
                      <Card key={type}>
                        <Message as={CardTitle} message={notificationTypeMessages[type].title} />
                        <div className={element('toggle-sms')}>
                          <Switch name="userSmsSubscriptionStatus" id="userSmsSubscriptionStatus">
                            {() => (
                              <Message
                                as="label"
                                className="ml-s"
                                message={appMessages.enable}
                                htmlFor="userSmsSubscriptionStatus"
                              />
                            )}
                          </Switch>
                        </div>
                        <Message as="p" className="text--dark" message={notificationTypeMessages[type].disclaimer} />
                        {tagTrees[type].length === 1 && (
                          <SmsSingleVenueSync venueId={tagTrees[type][0].id} enabled={isSmsEnabled} />
                        )}
                        {(isSmsEnabled || receiveNotifications.canReceiveSms.length > 0 || userPhoneNumber) && (
                          <>
                            {tagTrees[type].length > 1 && (
                              <>
                                <Message
                                  as="p"
                                  className="text--light"
                                  message={notificationTypeMessages[type].description}
                                />
                                <SmsVenueDropdown tree={tagTrees[type]} enabled={isSmsEnabled} />
                              </>
                            )}
                            <PhoneNumberInputSection disabled={!isSmsEnabled} required={isSmsEnabled} />
                          </>
                        )}
                      </Card>
                    );
                  }
                  return (
                    <NotificationGroup
                      key={type}
                      type={type}
                      notificationTreeName={`receiveNotifications.${type}`}
                      tree={tagTrees[type]}
                      notificationMessage={notificationTypeMessages[type]}
                      showTitle
                    />
                  );
                })}
                <SaveBar visible={isDirty} submitting={isSubmitting} formName={FORM_NAME} />
              </>
            );
          }}
        </Form>
      )}
      <MarketingEmails />
    </>
  );
};

Notifications.propTypes = {
  isLoading: PropTypes.bool,
  notificationTypes: PropTypes.arrayOf(PropTypes.oneOf(NOTIFICATION_TYPE_LIST)),
  tagTrees: PropTypes.shape(NOTIFICATION_TYPE_LIST.reduce((memo, type) => ({ ...memo, [type]: tagsPropTypes }), {})),
  receiveNotifications: PropTypes.shape(
    NOTIFICATION_TYPE_LIST.reduce((memo, type) => ({ ...memo, [type]: PropTypes.arrayOf(PropTypes.string) }), {})
  ),
  userSmsSubscriptionStatus: PropTypes.bool,
  userPhoneNumber: PropTypes.string,
  handleSubmit: PropTypes.func,
};

export default Notifications;
